import z from 'zod';

import type { UsabilityTier } from './lib/types.js';
import { AdditionalityApproachType } from './unit-meta/index.js';

export enum ComplianceRegimeType {
  Corsia = 'corsia',
  EuEts = 'eu-ets',
  EuEtsSupport = 'eu-ets-support',
  UkEts = 'uk-ets',
  IllinoisPurchaseCredits = 'illinois-purchase-credits',
  NewZealandEts = 'new-zealand-ets',
  None = 'none',
  Other = 'other',
}

export const ComplianceRegimeToLabel: Record<ComplianceRegimeType, string> = {
  [ComplianceRegimeType.Corsia]: 'ICAO CORSIA',
  [ComplianceRegimeType.EuEts]: 'EU ETS',
  [ComplianceRegimeType.UkEts]: 'UK ETS',
  [ComplianceRegimeType.IllinoisPurchaseCredits]: 'Illinois SAF Purchase Credits',
  [ComplianceRegimeType.Other]: 'Other',
  [ComplianceRegimeType.EuEtsSupport]: 'EU ETS SAF Support Mechanism',
  [ComplianceRegimeType.NewZealandEts]: 'New Zealand ETS',
  [ComplianceRegimeType.None]: 'None',
};

export const claimMetaComplianceRegimeToString = (complianceRegime: ComplianceRegime[]): string => {
  return complianceRegime.map((regime) => {
    if (regime.type === ComplianceRegimeType.Other) {
      return `${ComplianceRegimeToLabel[regime.type]} (${regime.name ?? ''})`;
    }

    return ComplianceRegimeToLabel[regime.type];
  }).join(', ');
};

export const ZodComplianceRegime = z.object({
  type: z.enum(ComplianceRegimeType),
  /** Name is set only if ComplianceRegimeType is Other; no union -> simpler typing */
  name: z.string().min(1).optional(),
}).refine((data) => {
  if (data.type === ComplianceRegimeType.Other) {
    return data.name !== undefined;
  } else {
    return data.name === undefined;
  }
}, { error: 'If and only if compliance regime is set to Other, compliance regime name has to be set', path: ['name'] });

export type ComplianceRegime = z.infer<typeof ZodComplianceRegime>;

export const ZodExternalClaimBeneficiary = z.object({ name: z.string().trim(), email: z.email().trim() });
export const ZodInternalClaimBeneficiary = z.object({ ownerId: z.string() });
export type InternalClaimBeneficiary = z.infer<typeof ZodInternalClaimBeneficiary>;
export type ExternalClaimBeneficiary = z.infer<typeof ZodExternalClaimBeneficiary>;
export const ZodClaimBeneficiary = z.union([
  ZodInternalClaimBeneficiary,
  ZodExternalClaimBeneficiary,
]);
export type ClaimBeneficiary = z.infer<typeof ZodClaimBeneficiary>;

export enum ClaimPurpose {
  Domestic = 'domestic',
  International = 'international',
}

export const ZodClaimMetadata = z.object({
  blockId: z.string(),
  volume: z.number().min(0.001),
  accountId: z.string(),
  retirementYear: z.number().min(new Date().getFullYear() - 2).max(new Date().getFullYear() + 2),
  retirementCountry: z.string(),
  retirementOnRegistryYear: z.number().optional(),
  claimPurpose: z.enum(ClaimPurpose).optional(),
  /**
   * @NOTE compliance regime is set only on SAFcA, not SAFcE
   * @TODO clean up this typing
   */
  complianceRegime: z.array(ZodComplianceRegime).min(1).optional(),
  logisticsProviderBeneficiary: z.string().optional(),
  previousOwnerId: z.string().optional(),
  safcClaimBeneficiary: ZodClaimBeneficiary.optional(),
  sercClaimBeneficiary: ZodClaimBeneficiary.optional(),
});

export type ClaimMetadata = z.infer<typeof ZodClaimMetadata>;

export const claimNonUpdatablePaths = [
  'blockId',
  'volume',
  'accountId',
  'retirementYear',
  'retirementCountry',
  'retirementOnRegistryYear',
  'claimPurpose',
  'complianceRegime.type',
  'complianceRegime.name',
  'logisticsProviderBeneficiary',
  'safcClaimBeneficiary.ownerId',
  'sercClaimBeneficiary.ownerId',
];

export const getAvailableComplianceRegimeTypes = ({
  additionalityApproach,
  rootUsabilityTier,
}: {
  additionalityApproach: AdditionalityApproachType | undefined | null;
  rootUsabilityTier: UsabilityTier;
}) => {
  if (additionalityApproach === AdditionalityApproachType.NonSABA) {
    // nonSABA means susTier C (SCS) so no condition for it although original requirements mention SCS
    return [
      ComplianceRegimeType.Corsia,
      ComplianceRegimeType.EuEts,
      ComplianceRegimeType.EuEtsSupport,
      ComplianceRegimeType.NewZealandEts,
      ComplianceRegimeType.IllinoisPurchaseCredits,
      ComplianceRegimeType.UkEts,
      ComplianceRegimeType.None,
      ComplianceRegimeType.Other,
    ];
  }

  // if additionalityApproach is not defined (old units)
  // then use the same logic as SABA

  if (rootUsabilityTier === 1) {
    // only SABA
    return [
      ComplianceRegimeType.Corsia,
      ComplianceRegimeType.EuEts,
      ComplianceRegimeType.NewZealandEts,
      ComplianceRegimeType.UkEts,
      ComplianceRegimeType.None,
    ];
  }

  if (rootUsabilityTier === 2) {
    // only SABA
    return [
      ComplianceRegimeType.EuEts,
      ComplianceRegimeType.EuEtsSupport,
      ComplianceRegimeType.IllinoisPurchaseCredits,
      ComplianceRegimeType.NewZealandEts,
      ComplianceRegimeType.UkEts,
      ComplianceRegimeType.None,
    ];
  }

  if (rootUsabilityTier === 3) {
    // unit is never issued as tier 3
    return [];
  }

  throw new Error(`Unreachable: ${additionalityApproach}${rootUsabilityTier}`);
};
